import { useMemo, useState } from 'react';
import { List, ListItemButton } from '@mui/material';
import { ChevronRightTwoTone } from '@mui/icons-material';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { ActionItemListProps } from '@components/DataGrid/Dispatch/types';
import DynamicSelectContainer from '@components/DynamicSelectContainer';

import styles from '@components/DataGrid/RenderCell.module.css';
import dispatchStyles from '@components/DataGrid/Dispatch/DataGrid.Dispatch.module.css';
import { parseEquipmentNumber } from '@api/Trinium/types';

/**
 * Copy Container Equipment Numbers from selected dispatch leg(s)
 */
export default function CopyContainerAction({ onClose }: ActionItemListProps) {
	const gridApiRef = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const containerEquipmentNos = useMemo(
		() =>
			Array.from(selectedRowIds)
				.map(([id]) => {
					const leg: DispatchLeg | null = gridApiRef.current.getRow(id);

					return leg?.cntrId;
				})
				.filter((v) => v),
		[selectedRowIds, gridApiRef]
	);

	const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(null);

	if (containerEquipmentNos.length === 0) {
		return <></>;
	}

	const copyContainer = (includeSuffix: boolean) => {
		const selectedValues = containerEquipmentNos.map((containerNumber) => {
			const containerEquipmentNo = parseEquipmentNumber(containerNumber);
			const cntrPrefix = containerEquipmentNo?.prefix || '';
			const cntrNo = containerEquipmentNo?.number || '';
			const cntrSuffix = containerEquipmentNo?.suffix || '';

			return `${cntrPrefix}${cntrNo}${includeSuffix ? cntrSuffix : ''}`;
		});
		// conversion to Set removes any duplicate values
		const result = [...new Set(selectedValues)].join('\n');
		void navigator.clipboard.writeText(result);
		onClose();
	};

	return (
		<ListItemButton
			onClick={(event) => {
				if (anchorEl == null) setAnchorEl(event.currentTarget);
				else setAnchorEl(null);
				event.stopPropagation();
			}}
		>
			{`Copy Container${containerEquipmentNos.length > 1 ? 's' : ''}`}
			<ChevronRightTwoTone
				fontSize='small'
				className={dispatchStyles['right-justified']}
			/>
			<DynamicSelectContainer
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				anchorOptions={{
					anchorOrigin: {
						vertical: 'top',
						horizontal: 'right'
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'left'
					}
				}}
			>
				<List
					dense
					disablePadding
					className={styles.actions}
				>
					<ListItemButton
						onClick={() => {
							copyContainer(true);
						}}
					>
						Include Check Digits
					</ListItemButton>
					<ListItemButton
						onClick={() => {
							copyContainer(false);
						}}
					>
						Exclude Check Digits
					</ListItemButton>
				</List>
			</DynamicSelectContainer>
		</ListItemButton>
	);
}
